import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchRequests } from "../../hooks/club-network/useSearchRequests";
import { usePermission } from "../../hooks/usePermission";
import { useViewMode } from "../../hooks/useViewMode";
import SearchRequestCard from "../../components/club-network/SearchRequestCard";
import SearchRequestListRow from "../../components/club-network/SearchRequestListRow";
import CloseSearchRequestModal from "../../components/club-network/CloseSearchRequestModal";
import WithdrawSearchRequestModal from "../../components/club-network/WithdrawSearchRequestModal";
import ViewModeToggle from "../../components/common/ViewModeToggle";
import { MagnifierGlyph } from "../../components/club-network/v2/cnGlyphs";
import { isWinnerRecipient, requestHasWinner } from "../../utils/searchRequestFormat";

// N3 Section-Home — the "My search requests" board (Commissioned Search, Annex
// A1/A15/A16), built on the Redesign 2.0 N3-4 card. Two tab dimensions:
//   • A15 Active/Archive — the lifecycle bucket the backend derives (loaded via
//     ?bucket=, counts from active_count/archive_count). NOT ?status=closed.
//   • A16 status groups (All / Waiting / Searching / Delivered / …) — a
//     client-side overview grouping over the loaded bucket; the chip row scrolls
//     horizontally on mobile (M·A16). Each row is the condensed A1 card.
// Close (A4) and Withdraw (A14b) are wired here through useSearchRequests.

// A16 request-level classification → its status group. Read from the recipient
// chain (the same source the A1 card renders), so the overview never drifts.
const classifyGroup = (req) => {
  const recs = req.recipients || [];
  const winner = recs.find(isWinnerRecipient);
  if (winner) {
    if (winner.status === "delivered") return "delivered";
    if (winner.status === "nothing_found") return "nothing_found";
    return "searching";
  }
  if (req.status === "closed") return "closed";
  if (recs.length && recs.every((r) => ["declined", "expired"].includes(r.status)))
    return "declined";
  return "waiting";
};

// Display order + label for the A16 group chips.
const GROUP_ORDER = [
  ["waiting", "Waiting"],
  ["searching", "Searching"],
  ["delivered", "Delivered"],
  ["nothing_found", "Nothing found"],
  ["closed", "Closed"],
  ["declined", "All declined"],
];

const TabStrip = ({ tabs, active, onChange }) => (
  <div className="flex items-center gap-4 md:gap-6 border-b border-[#E2E8F0] overflow-x-auto overflow-y-hidden">
    {tabs.map(({ key, label, count }) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        className={`pb-[9px] text-[13.5px] whitespace-nowrap border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
          active === key
            ? "border-[#2283FF] text-[#0F172A] font-bold"
            : "border-transparent text-[#64748B] font-medium hover:text-gray-800"
        }`}
      >
        {label}
        {count != null && (
          <span
            className={
              active === key
                ? "text-[11px] font-extrabold bg-[#2283FF] text-white rounded-full px-[7px] py-px"
                : "text-[#94A3B8]"
            }
          >
            {count}
          </span>
        )}
      </button>
    ))}
  </div>
);

// A16 group chips — horizontally scrollable (M·A16), never wrap-shrunk.
const GroupChips = ({ chips, active, onChange }) => (
  <div className="flex items-center gap-2 overflow-x-auto overflow-y-hidden -mx-1 px-1 pb-0.5">
    {chips.map(({ key, label, count }) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        className={`flex-none whitespace-nowrap rounded-full text-[12px] font-semibold px-3 py-1.5 border transition-colors ${
          active === key
            ? "bg-[#111827] text-white border-[#111827]"
            : "bg-white text-[#4B5563] border-[#E5E7EB] hover:bg-[#F3F4F6]"
        }`}
      >
        {label} · {count}
      </button>
    ))}
  </div>
);

const TrustedAgentHome = () => {
  const navigate = useNavigate();
  const [bucket, setBucket] = useState("active"); // A15 Active/Archive
  const [group, setGroup] = useState("all"); // A16 status group
  const {
    requests,
    counts = { active: null, archive: null },
    loading,
    error,
    fetchRequests,
    closeRequest,
    withdrawRequest,
  } = useSearchRequests(bucket);
  const canCreate = usePermission("create_tenders");
  const [viewMode, setViewMode] = useViewMode("trustedAgentViewMode");
  const [closing, setClosing] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [busy, setBusy] = useState(false);

  // A16: build the group-chip set from what's actually in the bucket (+ "All").
  const groupChips = useMemo(() => {
    const tally = {};
    requests.forEach((r) => {
      const g = classifyGroup(r);
      tally[g] = (tally[g] || 0) + 1;
    });
    const chips = [{ key: "all", label: "All", count: requests.length }];
    GROUP_ORDER.forEach(([key, label]) => {
      if (tally[key]) chips.push({ key, label, count: tally[key] });
    });
    return chips;
  }, [requests]);

  const visible = useMemo(
    () => (group === "all" ? requests : requests.filter((r) => classifyGroup(r) === group)),
    [requests, group],
  );

  const switchBucket = (b) => {
    setBucket(b);
    setGroup("all");
  };

  const confirmClose = async () => {
    setBusy(true);
    await closeRequest(closing.id);
    setBusy(false);
    setClosing(null);
  };

  const confirmWithdraw = async () => {
    setBusy(true);
    await withdrawRequest(withdrawing.id);
    setBusy(false);
    setWithdrawing(null);
  };

  const bucketTabs = [
    { key: "active", label: "Active", count: counts.active },
    { key: "archive", label: "Archive", count: counts.archive },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 md:py-6">
        <div className="flex items-start justify-between gap-4 mb-3.5">
          <div className="flex flex-col gap-1 min-w-0">
            <h1 className="text-[15px] md:text-[18px] font-bold text-[#111827]">
              My search requests
            </h1>
            <p className="hidden md:block text-[12.5px] text-[#6B7280]">
              Ask agents you trust to search for a player. After accept, everything
              lives in the chat.
            </p>
          </div>
          {canCreate && (
            <>
              <button
                onClick={() => navigate("/network/trusted-agent/new")}
                className="hidden md:inline-flex px-4 py-[9px] bg-[#2283FF] text-white rounded-lg text-[13px] font-semibold hover:bg-[#1B6FE0] flex-none"
              >
                New search request
              </button>
              <button
                onClick={() => navigate("/network/trusted-agent/new")}
                aria-label="New search request"
                className="md:hidden w-7 h-7 rounded-full bg-[#2283FF] text-white text-[16px] font-semibold flex items-center justify-center flex-none leading-none"
              >
                +
              </button>
            </>
          )}
        </div>

        {/* A15 Active / Archive tabs — always visible so the club can cross over
            even from an empty bucket. */}
        <div className="mb-3">
          <TabStrip tabs={bucketTabs} active={bucket} onChange={switchBucket} />
        </div>

        {loading ? (
          <div className="flex justify-center py-20 text-gray-500">Loading…</div>
        ) : error ? (
          <div className="flex flex-col items-center py-20 text-center gap-3">
            <p className="text-red-500">{error}</p>
            <button
              onClick={fetchRequests}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Try again
            </button>
          </div>
        ) : requests.length === 0 ? (
          bucket === "archive" ? (
            <div className="bg-white border border-[#D6DBE3] rounded-xl px-8 py-[38px] flex flex-col items-center gap-[11px] text-center">
              <h3 className="text-[15px] font-bold text-[#111827]">
                Nothing in the archive yet
              </h3>
              <p className="text-[12.5px] text-[#6B7280] leading-[1.6] max-w-sm">
                Delivered, closed and declined requests move here. Their proposals
                and reasons stay readable.
              </p>
            </div>
          ) : (
            // Empty state — board N3-4e A: 50px #E8F1FF disc + S3 magnifier BLUE.
            <div className="bg-white border border-[#D6DBE3] rounded-xl px-8 py-[38px] flex flex-col items-center gap-[11px] text-center">
              <span className="w-[50px] h-[50px] rounded-full bg-[#E8F1FF] flex items-center justify-center">
                <MagnifierGlyph size={19} stroke="#2283FF" />
              </span>
              <h3 className="text-[15px] font-bold text-[#111827]">
                No search requests yet
              </h3>
              <p className="text-[12.5px] text-[#6B7280] leading-[1.6] max-w-sm">
                Describe the player you're looking for and send it to agents you
                trust — they accept and start searching for you.
              </p>
              {canCreate && (
                <button
                  onClick={() => navigate("/network/trusted-agent/new")}
                  className="mt-1 px-[18px] py-2.5 bg-[#2283FF] text-white rounded-lg text-[13px] font-semibold hover:bg-[#1B6FE0]"
                >
                  New search request
                </button>
              )}
            </div>
          )
        ) : (
          <>
            {/* A16 status-group chips + the list/card toggle. */}
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="min-w-0 flex-1">
                <GroupChips chips={groupChips} active={group} onChange={setGroup} />
              </div>
              <div className="flex-none">
                <ViewModeToggle value={viewMode} onChange={setViewMode} />
              </div>
            </div>
            {viewMode === "list" ? (
              <div className="flex flex-col gap-2">
                {visible.map((r) => (
                  <SearchRequestListRow key={r.id} request={r} onClose={setClosing} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-3 md:gap-3.5">
                {visible.map((r) => (
                  <SearchRequestCard
                    key={r.id}
                    request={r}
                    onClose={setClosing}
                    onWithdraw={setWithdrawing}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {closing && (
        <CloseSearchRequestModal
          onCancel={() => setClosing(null)}
          onConfirm={confirmClose}
          busy={busy}
          running={requestHasWinner(closing)}
        />
      )}
      {withdrawing && (
        <WithdrawSearchRequestModal
          onCancel={() => setWithdrawing(null)}
          onConfirm={confirmWithdraw}
          busy={busy}
        />
      )}
    </div>
  );
};

export default TrustedAgentHome;
